import { useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import type { ResourceSermon, ResourceAsset } from './useSeriesDetail';

export class ImportLimitError extends Error {
  constructor(public reason: 'already_imported' | 'monthly_limit_reached') {
    super(reason === 'already_imported'
      ? 'You have already imported this series this month.'
      : 'You have reached your 2 series/month import limit.');
    this.name = 'ImportLimitError';
  }
}

async function importAsset(asset: ResourceAsset, sermonId: string): Promise<void> {
  const { data: signedData, error: signedError } = await supabase.storage
    .from('admin-assets')
    .createSignedUrl(asset.file_path, 120);

  if (signedError || !signedData) throw new Error(`Failed to get signed URL: ${signedError?.message}`);

  const response = await fetch(signedData.signedUrl);
  if (!response.ok) throw new Error(`Failed to fetch asset: ${response.statusText}`);
  const blob = await response.blob();
  const file = new File([blob], asset.file_name, { type: asset.mime_type ?? blob.type });

  const ext = asset.file_name.split('.').pop();
  const filePath = `${sermonId}/ai-generated/${uuidv4()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('sermon-media')
    .upload(filePath, file, { contentType: file.type });

  if (uploadError) throw new Error(`Failed to upload asset: ${uploadError.message}`);

  const { data: { publicUrl } } = supabase.storage.from('sermon-media').getPublicUrl(filePath);

  await supabase.from('sermon_media').insert({
    sermon_id: sermonId,
    section_id: 'ai-generated',
    file_name: asset.file_name,
    file_path: filePath,
    file_url: publicUrl,
    file_type: file.type,
    file_size: file.size,
  });
}

export function useImportSeries() {
  const importSingleSermon = useCallback(async (resourceSermonId: string): Promise<string> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: resourceSermon, error: sermonError } = await supabase
      .from('resource_sermons')
      .select('*')
      .eq('id', resourceSermonId)
      .single();

    if (sermonError || !resourceSermon) throw new Error('Sermon not found');

    const { data: newSermon, error: insertError } = await supabase
      .from('sermons')
      .insert({
        title: resourceSermon.title,
        scripture: resourceSermon.scripture,
        content: resourceSermon.sermon_notes ?? '',
        status: 'draft',
        user_id: user.id,
      })
      .select('id')
      .single();

    if (insertError || !newSermon) throw new Error(`Failed to create sermon: ${insertError?.message}`);

    const { data: assets } = await supabase
      .from('resource_assets')
      .select('*')
      .eq('sermon_id', resourceSermonId);

    if (assets?.length) {
      await Promise.all(assets.map(a => importAsset(a as ResourceAsset, newSermon.id)));
    }

    return newSermon.id;
  }, []);

  const importFullSeries = useCallback(async (
    seriesId: string,
    onProgress?: (step: string) => void
  ): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

    const [countResult, settingResult, existingResult] = await Promise.all([
      supabase.rpc('get_user_series_import_count'),
      supabase.from('series_import_settings').select('value').eq('key', 'monthly_import_limit').single(),
      supabase.from('series_import_log')
        .select('id')
        .eq('user_id', user.id)
        .eq('series_id', seriesId)
        .gte('imported_at', thirtyDaysAgo)
        .maybeSingle(),
    ]);

    if (existingResult.data) throw new ImportLimitError('already_imported');
    const limit = parseInt(settingResult.data?.value ?? '2');
    if ((countResult.data ?? 0) >= limit) throw new ImportLimitError('monthly_limit_reached');

    onProgress?.('Creating sermons in your library...');

    const [sermonsResult, assetsResult] = await Promise.all([
      supabase.from('resource_sermons').select('*').eq('series_id', seriesId).order('sermon_order'),
      supabase.from('resource_assets').select('*').eq('series_id', seriesId),
    ]);

    if (sermonsResult.error) throw sermonsResult.error;
    if (assetsResult.error) throw assetsResult.error;

    const resourceSermons = sermonsResult.data as ResourceSermon[];
    const allAssets = assetsResult.data as ResourceAsset[];

    const seriesLevelAssets = allAssets.filter(a => a.sermon_id === null);

    onProgress?.('Importing sermon notes...');

    const sermonIdMap: Record<string, string> = {};
    for (const rs of resourceSermons) {
      const { data: newSermon, error: insertError } = await supabase
        .from('sermons')
        .insert({
          title: rs.title,
          scripture: rs.scripture,
          content: rs.sermon_notes ?? '',
          status: 'draft',
          user_id: user.id,
        })
        .select('id')
        .single();

      if (insertError || !newSermon) throw new Error(`Failed to create sermon: ${insertError?.message}`);
      sermonIdMap[rs.id] = newSermon.id;
    }

    onProgress?.('Copying graphics to your media library...');

    const firstSermonId = sermonIdMap[resourceSermons[0]?.id];
    if (firstSermonId && seriesLevelAssets.length > 0) {
      await Promise.all(seriesLevelAssets.map(a => importAsset(a, firstSermonId)));
    }

    for (const rs of resourceSermons) {
      const perSermonAssets = allAssets.filter(a => a.sermon_id === rs.id);
      const newSermonId = sermonIdMap[rs.id];
      if (newSermonId && perSermonAssets.length > 0) {
        await Promise.all(perSermonAssets.map(a => importAsset(a, newSermonId)));
      }
    }

    await supabase.from('series_import_log').insert({ user_id: user.id, series_id: seriesId });

    onProgress?.('Done!');
  }, []);

  return { importSingleSermon, importFullSeries };
}
